import React, { FC, ReactElement, useContext, useMemo } from 'react'
import CLanguageHandler from '../../../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, ReactState, UserInfoContext } from '../../../../../React/Common/Contexts'
import { UserInfoContextType } from '../../../../../React/Common/Contexts/UserInfoContext'
import { useFormContext, UseFormMethods } from '../../../../../Vendor/react-hook-form/dist/index.ie11'
import { ValidationErrors } from '../../../../../React/Common/Components/ValidationErrors/ValidationErrors'
import { PasswordChangeFieldNames } from '../Constants/FieldNames'
import { PasswordChangeLanguage } from '../../../../../../i18n'
import { Rules } from '../../../../../React/Common/Rules/Rules'
import { OutlinedPasswordTextInput } from '../../../Atoms/Inputs/OutlinedPasswordTextInput'
import { IsVarEmpty } from '../../../../../NonReact/utils/GlobalFunctions'

type Props = {
    accountResetIDState: ReactState<Nullable<string>>
}

export const PasswordChangeForm: FC<Props> = ({ accountResetIDState: [accountResetID] }: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler

    const userInfo: UserInfoContextType = useContext(UserInfoContext)

    const { watch, trigger, setValue }: UseFormMethods = useFormContext()

    const isAdmin: boolean = useMemo((): boolean => (userInfo?.Permissions?.Admin?.length ?? 0) > 0, [userInfo])

    return (
        <div className='pc-form'>
            {IsVarEmpty(accountResetID) ? (
                <ValidationErrors name={PasswordChangeFieldNames.OLD_PASSWORD}>
                    <OutlinedPasswordTextInput
                        inputName={PasswordChangeFieldNames.OLD_PASSWORD}
                        isAdmin={isAdmin}
                        labelLeft={language.get(PasswordChangeLanguage.PasswordChange_Fields_OldPassword)}
                        isPasswordCheckerEnabled={false}
                        isRandomiserEnabled={false}
                        alwaysShowPasswordVisibilityOption
                        rules={{
                            required: language.get(PasswordChangeLanguage.PasswordChange_Validations_CurrentRequired)
                        }}
                        tabIndex={1}
                    />
                </ValidationErrors>
            ) : (
                <></>
            )}
            <ValidationErrors name={PasswordChangeFieldNames.NEW_PASSWORD}>
                <OutlinedPasswordTextInput
                    inputName={PasswordChangeFieldNames.NEW_PASSWORD}
                    isAdmin={isAdmin}
                    labelLeft={language.get(PasswordChangeLanguage.PasswordChange_Fields_NewPassword)}
                    onChange={(): void => {
                        setTimeout((): void => {
                            trigger(PasswordChangeFieldNames.CONFIRM_PASSWORD)
                        }, 100)
                    }}
                    isRandomiserAdornment
                    isRandomiserEnabled
                    isPasswordCheckerEnabled
                    alwaysShowPasswordVisibilityOption
                    onAutoGeneratePassword={(autoGeneratedPassword: string): void => {
                        setValue(PasswordChangeFieldNames.CONFIRM_PASSWORD, autoGeneratedPassword)

                        setTimeout((): void => {
                            trigger(PasswordChangeFieldNames.CONFIRM_PASSWORD)
                        }, 100)
                    }}
                    rules={{
                        required: language.get(PasswordChangeLanguage.PasswordChange_Validations_NewRequired)
                    }}
                    tabIndex={2}
                />
            </ValidationErrors>
            <ValidationErrors name={PasswordChangeFieldNames.CONFIRM_PASSWORD}>
                <OutlinedPasswordTextInput
                    inputName={PasswordChangeFieldNames.CONFIRM_PASSWORD}
                    isAdmin={isAdmin}
                    labelLeft={language.get(PasswordChangeLanguage.PasswordChange_Fields_ConfirmPassword)}
                    isPasswordCheckerEnabled={false}
                    isRandomiserEnabled={false}
                    alwaysShowPasswordVisibilityOption
                    rules={{
                        required: language.get(PasswordChangeLanguage.PasswordChange_Validations_ConfirmRequired),
                        validate: {
                            passwordsMustMatch: Rules.mustMatch(
                                language,
                                PasswordChangeLanguage.PasswordChange_Validations_PasswordsMustMatch,
                                watch(PasswordChangeFieldNames.NEW_PASSWORD)
                            )
                        }
                    }}
                    tabIndex={3}
                />
            </ValidationErrors>
        </div>
    )
}
